import { CommonEventOptions } from '@westwing/appshell_types';
import { useState } from 'react';
import { ClubRegistrationStatus, SessionTypes, UserLoginDataResponse } from '../types/types';
import {
    FormFieldTracking,
    FormInteractions,
    FormLinkTracking,
    PrivacyPolicyAcceptanceLabel,
    OverlayActions,
    OverlayDismissLabel,
    ShopToClubRegistrationResolutions,
    OverlayType,
    OverlayShownLabel,
    SIGNUP_AND_LOGIN_EVENT,
    EMPTY_LABEL,
    SubmitAndLoginActions,
    LoginType,
} from 'Interfaces/overlayTracking';
import { eventOptionsToString } from 'Helpers/trackingHelper';
import { useGaTracking } from 'AppShell/TrackingHelper';

export interface SignupFormTrackingHook {
    trackRegistrationSuccess: (status: ClubRegistrationStatus) => void;
    trackLoginSuccess: (userData: UserLoginDataResponse) => void;
    trackOverlayShown: (label: OverlayShownLabel) => void;
    trackOverlayDismiss: (label: OverlayDismissLabel) => void;
    trackPrivacyPolicyShown: () => void;
    trackPrivacyPolicyAcceptance: (isTermsAccepted: boolean) => void;
    trackFieldFilled: (field: FormFieldTracking) => void;
    trackFieldError: (field: FormFieldTracking) => void;
    trackFieldChecked: (field: FormFieldTracking) => void;
    trackLinkClick: (link: FormLinkTracking) => void;
    trackFormUnfolded: () => void;
    trackPrivacyPolicyScroll: () => void;
    trackFormSubmit: (isSubmit?: boolean) => void;
    trackLegalTextExpand: (isSubmit?: boolean) => void;
}

const useSignupFormTracking = (): SignupFormTrackingHook => {
    const { trackGenericEvent } = useGaTracking();

    const [trackedEvents, setTrackedEvents] = useState<{ [event: string]: boolean }>({});

    const trackShopToClub = (options: CommonEventOptions) => {
        trackGenericEvent(OverlayType.ShopToClubOverlay, options);
    };

    const trackShopToClubOnce = (options: CommonEventOptions) => {
        const eventHash = eventOptionsToString(options);
        if (!trackedEvents[eventHash]) {
            trackShopToClub(options);
            const nextEvents = trackedEvents;
            nextEvents[eventHash] = true;
            setTrackedEvents(nextEvents);
        }
    };

    const trackRegistrationSuccess = (status: ClubRegistrationStatus) => {
        let label = ClubRegistrationStatus.NEW_REGISTRATION
            ? ShopToClubRegistrationResolutions.New
            : ShopToClubRegistrationResolutions.Login;

        if (status === ClubRegistrationStatus.WRONG_PASSWORD) {
            label = ShopToClubRegistrationResolutions.WrongPassword;
        }

        trackShopToClubOnce({
            action: OverlayActions.Registration,
            label,
        });
    };

    const trackLoginSuccess = (userData: UserLoginDataResponse) => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: SubmitAndLoginActions.Login,
            label: userData?.sessionType === SessionTypes.partial ? LoginType.ShopPartialLogin : LoginType.ShopLogin,
        });
    };

    const trackOverlayDismiss = (label: OverlayDismissLabel) => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.OverlayDismissed,
            label,
        });
    };

    const trackPrivacyPolicyShown = () => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.PrivacyPolicyShown,
            label: EMPTY_LABEL,
        });
    };

    const trackPrivacyPolicyAcceptance = (isClubNewsletterSubscribed: boolean) => {
        const label: PrivacyPolicyAcceptanceLabel = isClubNewsletterSubscribed ? 'Accepted' : 'Not accepted';
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.PrivacyPolicyAcceptance,
            label,
        });
    };

    const trackFieldFilled = (field: FormFieldTracking) => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormFieldChanged,
            label: field,
        });
    };

    const trackFieldError = (field: FormFieldTracking) => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormFieldError,
            label: field,
        });
    };

    const trackFieldChecked = (field: FormFieldTracking) => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormFieldChanged,
            label: field,
        });
    };

    const trackLinkClick = (link: FormLinkTracking) => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormLinkClicked,
            label: link,
        });
    };

    const trackFormUnfolded = () => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormInteraction,
            label: FormInteractions.FormUnfolded,
        });
    };

    const trackPrivacyPolicyScroll = () => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormInteraction,
            label: FormInteractions.PrivacyPolicyScrolled,
        });
    };

    const trackFormSubmit = (isSubmit = true) => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormInteraction,
            label: isSubmit ? FormInteractions.SubmitButtonClicked : FormInteractions.ContinueButtonClicked,
        });
    };

    const trackLegalTextExpand = () => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormInteraction,
            label: FormInteractions.LegalTextExpanded,
        });
    };

    const trackOverlayShown = (label: OverlayShownLabel) => {
        trackShopToClubOnce({
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.OverlayShown,
            label,
        });
    };

    return {
        trackRegistrationSuccess,
        trackLoginSuccess,
        trackOverlayDismiss,
        trackOverlayShown,
        trackPrivacyPolicyShown,
        trackPrivacyPolicyAcceptance,
        trackFieldFilled,
        trackFieldError,
        trackFieldChecked,
        trackLinkClick,
        trackFormUnfolded,
        trackPrivacyPolicyScroll,
        trackFormSubmit,
        trackLegalTextExpand,
    };
};

export default useSignupFormTracking;
